import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { KeyValue } from '@angular/common';
import { User } from '../../models/user';
import { MountPoint } from '../../models/mount-point';
import { UserService } from '../../services/user.service';
import { DownloaderService } from '../../services/downloader.service';
import { PermissionService } from '../../services/permission.service';
import { MatDialog } from '@angular/material/dialog';
import { HelpDialogComponent } from '../../pages/help-dialog/help-dialog.component';
import { ConfirmDialogComponent } from '../../pages/confirm-dialog/confirm-dialog.component';
import { AcknowledgeDialogComponent } from '../../pages/acknowledge-dialog/acknowledge-dialog.component';
import { NtripConnection } from '../../models/ntrip-connection';
import { RtkBaseAnalyzerHelpDialogComponent } from '../rtk-base-analyzer/rtk-base-analyzer-help-dialog/rtk-base-analyzer-help-dialog.component';
import { PpkDownloaderHelpDialogComponent } from '../ppk-downloader/ppk-downloader-help-dialog/ppk-downloader-help-dialog.component';
import { PpkDownloaderStartTimeHelpDialogComponent } from '../ppk-downloader/ppk-downloader-start-time-help-dialog/ppk-downloader-start-time-help-dialog.component';

interface HelpData {
  [key: string]: {
    description: string;
    content: string;
  };
}

@Component({
  selector: 'app-user-mountpoint',
  templateUrl: './user-mountpoint.component.html',
  styleUrl: './user-mountpoint.component.scss'
})
export class UserMountpointComponent implements OnInit {
  @Input() user?: User; // 仅保留这一个输入属性来接收当前选中的用户对象
  @Input() pageTitle: string = 'My Mountpoints';
  // 控制各子分区显示（默认都显示，便于复用此组件生成专用页面）
  @Input() showDetails: boolean = true;
  @Input() showMap: boolean = true;
  @Input() showSnr: boolean = true;
  @Input() showAnalyzer: boolean = false;
  @Input() showDownloader: boolean = false;
  // 顶部按钮控制
  @Input() showAddDelButtons: boolean = true;
  @Input() showGetInfoButton: boolean = true;
  // Hide the top pagination navigation bar (First/Prev/Next/Last). Default to false since pages
  // like /user-mountpoint, /ppk-downloader, /rtk-base-analyzer don't need it.
  @Input() showPaginationNav: boolean = false;
  // 顶部信息框覆盖（用于 PPK Downloader 页面展示固定说明）
  @Input() introOverride?: string;
  @Input() introOverrideVariant: 'info' | 'warning' = 'info';
  // Analyzer page: auto show full JSON when available
  @Input() autoShowFullAnalysis: boolean = false;
  // Analyzer page mode to adjust copy/labels
  @Input() isAnalyzerPage: boolean = false;
  // Downloader page (basic edition) mode to use simplified layout
  @Input() isDownloaderPage: boolean = false;

  quickGuideExpanded = false;

  openAnalyzerHelp(): void {
    this.dialog.open(RtkBaseAnalyzerHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  openDownloaderHelp(): void {
    this.dialog.open(PpkDownloaderHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  openDownloaderStartTimeHelp(): void {
    this.dialog.open(PpkDownloaderStartTimeHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  toggleQuickGuide(): void {
    this.quickGuideExpanded = !this.quickGuideExpanded;
  }

  get stepMountPointStatus(): { label: string; tone: 'muted' | 'success' } {
    if (this.currentMountPoint?.stMountPoint) return { label: 'Selected', tone: 'success' };
    return { label: 'Not selected', tone: 'muted' };
  }

  get stepDurationStatus(): { label: string; tone: 'muted' | 'success' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not ready', tone: 'muted' };
    const dur = this.analyzerDurationByMountpoint[mp] || 120;
    if ((Number(dur) || 0) > 0) return { label: 'Ready', tone: 'success' };
    return { label: 'Not ready', tone: 'muted' };
  }

  get stepAnalyzeStatus(): { label: string; tone: 'muted' | 'info' | 'success' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not started', tone: 'muted' };
    if (this.analysisCountdownActive && this.analysisCountdownMountpoint === mp) return { label: 'Running', tone: 'info' };
    if (this.analysisJsonByMountpoint[mp]) return { label: 'Ready', tone: 'success' };
    return { label: 'Not started', tone: 'muted' };
  }

  get stepReportStatus(): { label: string; tone: 'muted' | 'success' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not loaded', tone: 'muted' };
    if (this.analysisJsonByMountpoint[mp]) return { label: 'Loaded', tone: 'success' };
    return { label: 'Not loaded', tone: 'muted' };
  }

  get dlStepMountPointStatus(): { label: string; tone: 'muted' | 'success' } {
    if (this.currentMountPoint?.stMountPoint) return { label: 'Selected', tone: 'success' };
    return { label: 'Not selected', tone: 'muted' };
  }

  get dlStepStartTimeStatus(): { label: string; tone: 'muted' | 'success' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not ready', tone: 'muted' };
    return this.isDownloaderStartTimeValid(mp) ? { label: 'Ready', tone: 'success' } : { label: 'Not ready', tone: 'muted' };
  }

  get dlStepDurationStatus(): { label: string; tone: 'muted' | 'success' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not ready', tone: 'muted' };
    const mins = this.getDurationSelection(mp);
    return (Number(mins) || 0) > 0 ? { label: 'Ready', tone: 'success' } : { label: 'Not ready', tone: 'muted' };
  }

  get dlStepSubmitStatus(): { label: string; tone: 'muted' | 'info' | 'success' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not submitted', tone: 'muted' };
    if (this.downloaderLoading[mp]) return { label: 'Submitting...', tone: 'info' };
    const dl = this.downloaderUrlByMountpoint[mp];
    if (dl?.running) return { label: 'Running', tone: 'info' };
    if (dl?.url && !this.isExpired(dl)) return { label: 'Ready', tone: 'success' };
    return { label: 'Not submitted', tone: 'muted' };
  }

  get dlStepDownloadStatus(): { label: string; tone: 'muted' | 'success' | 'danger' } {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return { label: 'Not ready', tone: 'muted' };
    const dl = this.downloaderUrlByMountpoint[mp];
    if (dl?.url) {
      if (this.isExpired(dl)) return { label: 'Expired', tone: 'danger' };
      return { label: 'Ready', tone: 'success' };
    }
    return { label: 'Not ready', tone: 'muted' };
  }

  getAnalyzeButtonLabel(mp: MountPoint): string {
    const key = mp?.stMountPoint;
    if (!key) return 'Analyze this RTK Base';
    const hasReport = !!this.analyzerSummaryByMountpoint[key] || !!this.analysisJsonByMountpoint[key];
    return hasReport ? 'Re-analyze this RTK Base' : 'Analyze this RTK Base';
  }
  canEdit = false;
  action: string = '';

  selectedMountPoint?: MountPoint; // 定义selectedMountPoint属性

  // Analysis 展示数据
  analysisJson: any = null;
  analysisJsonMountpoint: string = '';
  analysisJsonByMountpoint: { [key: string]: any } = {};
  // 自动提取的概要信息（不展示完整 JSON）
  analyzerSummaryByMountpoint: { [key: string]: { [k: string]: any } } = {};
  analyzerSummaryRequested: { [key: string]: boolean } = {};
  coordsByMountpoint: { [key: string]: { lat: number; lng: number } } = {};
  // 运行时实例缓存，便于来回切换时销毁与重建
  private mapInstances: { [key: string]: any } = {};
  private chartInstances: { [key: string]: any } = {};
  // store generated_at for message box per mountpoint
  generatedAtByMountpoint: { [key: string]: string } = {};
  // 顶部“Pull RTK Base Info”进度条（150秒）状态
  analysisCountdownActive: boolean = false;
  analysisCountdownRemaining: number = 0;
  private analysisCountdownTimer: any = null;
  analysisCountdownMountpoint: string = '';
  warmupRemaining: number = 0; // first 10s warmup with static label
  private readonly focusStorageKey: string = 'geoastra_mp_focus';
  analysisTotalSeconds: number = 150;
  private readonly focusQueryParam: string = 'mp';

  // keyvalue 管道比较器：返回 0 以保留插入顺序
  keyOrder(a: KeyValue<string, any>, b: KeyValue<string, any>): number { return 0; }

  isObject(val: any): boolean { return val && typeof val === 'object' && !Array.isArray(val); }
  isArray(val: any): boolean { return Array.isArray(val); }
  isUrl(val: any): boolean {
    return typeof val === 'string' && /^(https?:)?\/\//i.test(val);
  }

  // 展开/折叠控制
  defaultExpandedByMountpoint: { [key: string]: boolean } = {};
  nodeExpanded: { [mp: string]: { [path: string]: boolean } } = {};

  isExpanded(mp: string, path: string): boolean {
    const overrides = this.nodeExpanded[mp];
    if (overrides && Object.prototype.hasOwnProperty.call(overrides, path)) {
      return !!overrides[path];
    }
    return !!this.defaultExpandedByMountpoint[mp];
  }

  toggleNode(mp: string, path: string): void {
    if (!this.nodeExpanded[mp]) this.nodeExpanded[mp] = {};
    const currentlyExpanded = this.isExpanded(mp, path);
    this.nodeExpanded[mp][path] = !currentlyExpanded;
  }

  expandAll(mp: string): void {
    this.defaultExpandedByMountpoint[mp] = true;
    this.nodeExpanded[mp] = {};
  }

  collapseAll(mp: string): void {
    this.defaultExpandedByMountpoint[mp] = false;
    this.nodeExpanded[mp] = {};
  }

  copyJson(mp: string): void {
    try {
      const data = this.analysisJsonByMountpoint[mp];
      const text = JSON.stringify(data, null, 2);
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Analysis JSON copied to clipboard.' });
    } catch (e) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Copy failed.' });
    }
  }

  helpData: HelpData  = {
    type: {
      description: 'Indicator of a stream description',
      content: 'Content: STR'
    },
    mountpoint: {
      description: 'Mountpoint name. Must be unique across the system and can include up to 100 characters. Allowed characters are A-Z, a-z, 0-9, hyphens (-), underscores (_), and periods (.).',
      content: 'Text'
    },
    identifier: {
      description: 'Source identifier - usually use the name of the nearest city.',
      content: 'Text'
    },
    format: {
      description: 'Protocol used. For example: BINEX, CMR, NMEA, RAW, RTCA, RTCM 2.1, RTCM 2.2, RTCM 2.3, RTCM 3.0, RTCM 3.1, RTCM 3.2, RTCM 3.3',
      content: 'Text'
    },
    formatDetails: {
      description: 'Sentences used. For example: For example: 1005(1),1077(1),1087(1),1097(1),1127(1),1230(8)',
      content: 'Text'
    },
    carrier: {
      description: '0 - No phase information included; 1	- L1 phase included; 2 - L2 phase included.',
      content: 'Number'
    },
    navSystem: {
      description: 'Navigation System. For example: BDS, GAL, GLO, GPS, IRS, QZS, SBAS',
      content: 'Text'
    },
    network: {
      description: 'Network name: Typically, enter your organization name.',
      content: 'Text'
    },
    country: {
      description: 'Country of your RTK BaseStation location: Please use the ISO 3166 country code. For example: USA, CAN.',
      content: '3 characters'
    },
    latitude: {
      description: 'Latitude in degrees of your RTK BaseStation location.',
      content: 'Decimal number, two digits after dot'
    },
    longitude: {
      description: 'Longitude in degree of your RTK BaseStation location.',
      content: 'Decimal number, two digits after dot'
    },
    nmea: {
      description: 'Does this mountpoint require NMEA input? Yes -> 1; No -> 0.',
      content: '1 or 0'
    },
    solution: {
      description: 'RTK solution generated by? If single base -> 0; if network -> 1.',
      content: '1 or 0'
    },
    generator: {
      description: 'NTRIP correction data generated by what software or hardware',
      content: 'Text'
    },
    compression: {
      description: 'What compression algorithm is used?',
      content: 'Text'
    },
    authentication: {
      description: 'what Access protection is used for the data streams? None -> N, Basic -> B, Digest ->D',
      content: 'N, B or D'
    },
    fee: {
      description: 'Is this NTRIP service a paid service? Please indicate with Y for yes or N for no.',
      content: 'Y or N'
    },
    bitrate: {
      description: 'Datarate in bits per second. For example: 4,800',
      content: 'Number'
    },
    misc: {
      description: 'Miscellaneous information.',
      content: 'Text'
    }
  };

  constructor(private userService: UserService, private dialog: MatDialog, private downloaderService: DownloaderService, private perms: PermissionService) { }

  ngOnInit() {
    if (!this.user) {
      this.retrieveUserInit();
    }  
  }

  retrieveUser(): void {
    this.perms.ensureFresh();
    this.userService.getUser().subscribe((data: User[]) => {
      this.user = data[0];
      this.buildSortedMountPoints();
      this.ensurePageBounds();
      // 优先从URL参数恢复
      this.restoreFocusFromUrlIfAny();
      this.restoreFocusedMountpointIfAny();
      // 确保当前页的挂载点被选中并显示详情（例如删除后）
      this.triggerFetchForCurrent();
      this.fetchUserConnections();
    });
  }

  retrieveUserInit(): void {
    this.perms.ensureFresh();
    this.userService.getUser().subscribe((data: User[]) => {
      this.user = data[0];
      this.buildSortedMountPoints();
      this.ensurePageBounds();
      // 优先从URL参数恢复
      this.restoreFocusFromUrlIfAny();
      this.restoreFocusedMountpointIfAny();
      this.triggerFetchForCurrent();
      this.fetchUserConnections();
    });
  }

  selectMountPoint(mountPoint: MountPoint): void {
    // Reset previous
    const prev = this.selectedMountPoint?.stMountPoint;
    if (prev) this.resetDetailsStateFor(prev);
    // Select new and reset its UI state to default (collapsed + not editing)
    this.selectedMountPoint = mountPoint;
    this.resetDetailsStateFor(mountPoint?.stMountPoint);
  }

  editMountPoint(mountPoint: MountPoint): void {
    // Ensure selected, then enter edit mode and expand
    this.selectedMountPoint = mountPoint;
    this.canEdit = true;
    this.action = 'edit';
    const key = mountPoint?.stMountPoint || '';
    if (key) this.detailsCollapsedByMountpoint[key] = false;
  }

  // Toggle helpers for Show/Edit buttons
  getShowButtonLabel(mountPoint: MountPoint): string {
    const isVisible = this.selectedMountPoint === mountPoint && this.action === 'show' && !this.canEdit;
    return isVisible ? 'Hide Detail' : 'Show Detail';
  }
  onToggleShow(mountPoint: MountPoint): void {
    const isVisible = this.selectedMountPoint === mountPoint && this.action === 'show' && !this.canEdit;
    if (isVisible) {
      this.selectedMountPoint = undefined;
      this.action = '';
      this.canEdit = false;
    } else {
      this.selectMountPoint(mountPoint);
    }
  }
  getEditButtonLabel(mountPoint: MountPoint): string {
    const isEditing = this.selectedMountPoint === mountPoint && this.action === 'edit' && this.canEdit;
    return isEditing ? 'Hide Edit' : 'Edit';
  }
  onToggleEdit(mountPoint: MountPoint): void {
    const isEditing = this.selectedMountPoint === mountPoint && this.action === 'edit' && this.canEdit;
    if (isEditing) {
      this.selectedMountPoint = undefined;
      this.action = '';
      this.canEdit = false;
    } else {
      this.editMountPoint(mountPoint);
    }
  }

  // Collapse/expand control for MountPoint Details body
  private detailsCollapsedByMountpoint: { [key: string]: boolean } = {};
  // Map & SNR collapse states: default expanded (false)
  private mapCollapsedByMountpoint: { [key: string]: boolean } = {};
  isDetailsCollapsed(mp: string): boolean {
    // Default to expanded (false) until user collapses
    return this.detailsCollapsedByMountpoint[mp] === true;
  }
  toggleDetails(mp: string): void {
    const current = this.isDetailsCollapsed(mp);
    this.detailsCollapsedByMountpoint[mp] = !current;
  }

  private resetDetailsStateFor(mp: string | undefined): void {
    if (!mp) return;
    this.detailsCollapsedByMountpoint[mp] = false; // default expanded
    this.mapCollapsedByMountpoint[mp] = false;     // default expanded
    this.canEdit = false;
    this.action = '';
  }

  toggleDetailsSection(mountPoint: MountPoint): void {
    const key = mountPoint?.stMountPoint || '';
    if (!key) return;
    const current = this.isDetailsCollapsed(key);
    this.detailsCollapsedByMountpoint[key] = !current;
    if (!this.detailsCollapsedByMountpoint[key]) {
      // Expanded → ensure the correct mountpoint is selected for bindings
      this.selectedMountPoint = mountPoint;
      if (this.action !== 'edit') {
        this.action = 'show';
        this.canEdit = false;
      }
    }
  }

  // Map subsection helpers
  isMapCollapsed(mp: string): boolean { return !!this.mapCollapsedByMountpoint[mp]; }
  toggleMapSection(mp: string): void {
    this.mapCollapsedByMountpoint[mp] = !this.isMapCollapsed(mp);
    if (!this.isMapCollapsed(mp)) {
      // when expanding, ensure render
      this.scheduleRenderForMountpoint(mp);
    }
  }

  openConfirmDialog(mountPoint: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '360px',
      data: { title: 'Delete Mountpoint Confirmation', message: 'Are you sure you want to delete this mountpoint?' }
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteMountPoint(mountPoint); // 实际删除操作的函数
      }
    });
  }  

  // 新增的删除挂载点方法
  deleteMountPoint(mountPointToDelete: MountPoint): void {
    if (!this.user) {
      console.error('User is undefined; cannot delete mountpoint');
      return;
    }

    // 过滤掉要删除的挂载点
    this.user.mountPoints = this.user.mountPoints.filter(mountPoint => mountPoint !== mountPointToDelete);

    // Self-service update of the signed-in user's mountpoints
    this.userService.updateCurrentUser(this.user).subscribe({
      next: (response) => {
        console.info('Mountpoint deleted successfully');
        this.userService.ntripApplyWithLogging();
        // 删除成功后，定位到删除后当前页的第一项并显示详情
        setTimeout(() => this.retrieveUser(), 0);
      },
      error: (error) => {
        console.error('Error occurred while deleting mountpoint', error);
      }
    });
  }
  

  onSubmit(): void {
    if (this.user) {
      this.userService.updateCurrentUser(this.user).subscribe({
        next: () => {
          // 更新成功后，禁止编辑
          this.canEdit = false;
          this.dialog.open(AcknowledgeDialogComponent, {
            data: 'User update successful.'
          });
          this.userService.ntripApplyWithLogging();
        },
        error: (err) => {
          this.dialog.open(AcknowledgeDialogComponent, {
            data: `${err.error}`
          });
        }
      });
    } else {
      console.error('no user selected');
    }
  }

  generateNewMountPointForUser(): void {
    if (this.user) {
      this.userService.generateNewMountPoint().subscribe({
        next: (response) => {
          // Backend may return 200 with { created:false, message: '...' } when over license
          let body: any = (response as any)?.body ?? response;
          if (typeof body === 'string') {
            try { body = JSON.parse(body); } catch { /* keep raw */ }
          }
          const created = body && typeof body === 'object' && body.created === false ? false : true;
          const msg = body && typeof body === 'object' && typeof body.message === 'string'
            ? body.message
            : (created ? 'New mountpoint created successfully.' : 'Failed to create new mountpoint.');

          if (created) {
            const newMp = (body && typeof body === 'object' && typeof body.mountpoint === 'string') ? body.mountpoint : undefined;
            if (newMp) {
              // 记住并写入 URL，便于刷新后自动聚焦到新建挂载点并展开详情
              this.persistFocusMountpoint(newMp);
              this.persistFocusToUrl(newMp);
            }
            this.retrieveUser();
            this.userService.ntripApplyWithLogging();
          }
          this.dialog.open(AcknowledgeDialogComponent, { data: msg });
        },
        error: (error) => {
          console.warn('Failed to create new mountpoint:', error);
          this.dialog.open(AcknowledgeDialogComponent, {
            data: `Failed to create new mountpoint: ${error.error}`
          });
        }
      });
    } else {
      console.error('No selected user to create a new mountpoint');
    }
  }

  onBack(): void {
    this.canEdit = false;  // 禁止编辑并隐藏按钮
    this.action = 'show';
  }
  currentHelp = { description: '', content: '' };

  showHelp(key: string): void {
    const helpData = this.helpData[key];
    this.dialog.open(HelpDialogComponent, {
      width: '300px',
      data: helpData
    });
  }

  hideHelp(): void {
    this.currentHelp = { description: '', content: '' };
  }

  // -------- Downloader section state --------
  downloaderInputsByMountpoint: { [key: string]: { durationSec: number; planStartAt: string; scheduleLeadMin: number } } = {};
  downloaderLoading: { [key: string]: boolean } = {};
  downloaderUrlByMountpoint: { [key: string]: { url: string; expiresAt: string; lastModified?: string; running?: boolean } | null } = {};
  private downloaderPollTimers: { [key: string]: any } = {};
  private downloaderStartTimeRejectedByMountpoint: { [key: string]: boolean } = {};

  // ---- Download expiry helpers (7 days from lastModified) ----
  getExpiryDate(dl: { lastModified?: string } | null | undefined): Date | null {
    try {
      const iso = dl?.lastModified;
      if (!iso || typeof iso !== 'string') return null;
      const created = new Date(iso);
      if (isNaN(created.getTime())) return null;
      const expMs = created.getTime() + 7 * 24 * 60 * 60 * 1000;
      return new Date(expMs);
    } catch {
      return null;
    }
  }

  isExpired(dl: { lastModified?: string } | null | undefined): boolean {
    const exp = this.getExpiryDate(dl);
    if (!exp) return false;
    return Date.now() > exp.getTime();
  }

  ensureDownloaderDefaults(mp: string): void {
    if (!this.downloaderInputsByMountpoint[mp]) {
      this.downloaderInputsByMountpoint[mp] = {
        durationSec: 120,
        planStartAt: this.nowIsoWithOffset(),
        scheduleLeadMin: 1
      };
    }
    if (this.downloaderStartTimeRejectedByMountpoint[mp] == null) {
      this.downloaderStartTimeRejectedByMountpoint[mp] = false;
    }
  }

  // 在模板中安全获取下载输入，避免可选链告警
  downInputs(mp: string) {
    this.ensureDownloaderDefaults(mp);
    return this.downloaderInputsByMountpoint[mp];
  }

  // ----- Active indicator based on current NTRIP connections -----
  private userConnections: NtripConnection[] = [];
  private activeByMountpoint: { [key: string]: boolean } = {};

  private normalizeMp(name: string | undefined | null): string {
    return (name || '').replace(/^\/+/, '').trim().toLowerCase();
  }

  private buildActiveMap(): void {
    const map: { [key: string]: boolean } = {};
    for (const c of this.userConnections) {
      const type = (c?.type || '').toLowerCase();
      const mp = this.normalizeMp(c?.mountpoint);
      if (type === 'source' && mp) {
        map[mp] = true;
      }
    }
    this.activeByMountpoint = map;
  }

  private fetchUserConnections(): void {
    this.userService.getUserNtripConnections().subscribe({
      next: (list: NtripConnection[]) => { this.userConnections = list || []; this.buildActiveMap(); },
      error: () => { this.userConnections = []; this.activeByMountpoint = {}; }
    });
  }

  isMountpointActive(mp: string | undefined): boolean {
    if (!mp) return false;
    const key = this.normalizeMp(mp);
    return !!this.activeByMountpoint[key];
  }

  // Duration helpers (hours/minutes selectors with free input)
  getDurationHours(mp: string): number {
    const total = Number(this.downInputs(mp).durationSec) || 0;
    return Math.max(0, Math.min(24, Math.floor(total / 3600)));
  }
  getDurationMinutes(mp: string): number {
    const total = Number(this.downInputs(mp).durationSec) || 0;
    return Math.max(0, Math.min(59, Math.floor((total % 3600) / 60)));
  }
  onDurationHoursChange(mp: string, v: any): void {
    const hours = Math.max(0, Math.min(24, parseInt(v, 10) || 0));
    const minutes = this.getDurationMinutes(mp);
    this.downloaderInputsByMountpoint[mp].durationSec = hours * 3600 + minutes * 60;
  }
  onDurationMinutesChange(mp: string, v: any): void {
    const minutes = Math.max(0, Math.min(59, parseInt(v, 10) || 0));
    const hours = this.getDurationHours(mp);
    this.downloaderInputsByMountpoint[mp].durationSec = hours * 3600 + minutes * 60;
  }

  // datetime-local helpers: map between ISO8601(with offset) and input's local value
  private pad(n: number): string { return n < 10 ? '0' + n : String(n); }
  private nowFloorToMinuteMs(): number {
    const d = new Date();
    d.setSeconds(0, 0);
    return d.getTime();
  }
  private maxScheduleMs(): number {
    // Allow scheduling up to 7 days into the future (inclusive), evaluated from current minute.
    return this.nowFloorToMinuteMs() + 7 * 24 * 60 * 60 * 1000;
  }
  private isoWithOffsetFromDate(d: Date): string {
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? '+' : '-';
    const abs = Math.abs(tz);
    const hh = this.pad(Math.floor(abs / 60));
    const mm = this.pad(abs % 60);
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}${sign}${hh}:${mm}`;
  }
  private nowIsoWithOffset(): string {
    const d = new Date(); // default: now
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? '+' : '-';
    const abs = Math.abs(tz);
    const hh = this.pad(Math.floor(abs / 60));
    const mm = this.pad(abs % 60);
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}${sign}${hh}:${mm}`;
  }
  private isoToLocalInputValue(iso?: string): string | null {
    if (!iso || typeof iso !== 'string') return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
  }
  private localInputValueToIsoWithOffset(val?: string): string | null {
    if (!val || typeof val !== 'string') return null;
    // val like 2025-09-15T14:30 (no seconds). Treat as local time, add seconds, then append timezone offset
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const hour = Number(m[4]);
    const minute = Number(m[5]);
    const d = new Date(year, month - 1, day, hour, minute, 0);
    const tz = -d.getTimezoneOffset();
    const sign = tz >= 0 ? '+' : '-';
    const abs = Math.abs(tz);
    const hh = this.pad(Math.floor(abs / 60));
    const mm = this.pad(abs % 60);
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}T${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}${sign}${hh}:${mm}`;
  }

  getPlanStartAtInput(mp: string): string | null {
    const iso = this.downInputs(mp).planStartAt;
    return this.isoToLocalInputValue(iso) || null;
  }
  onPlanStartAtInputChange(mp: string, val: string): void {
    this.ensureDownloaderDefaults(mp);
    const prevIso = this.downInputs(mp).planStartAt;
    const prevLocal = this.isoToLocalInputValue(prevIso) || prevIso;
    const iso = this.localInputValueToIsoWithOffset(val) || this.nowIsoWithOffset();
    const chosen = new Date(iso);
    const ms = chosen.getTime();
    const min = this.nowFloorToMinuteMs();
    const max = this.maxScheduleMs();
    if (isNaN(ms)) {
      this.dialog.open(AcknowledgeDialogComponent, { data: `Invalid Recording Start Time. Keeping previous value (${prevLocal}).` });
      this.downInputs(mp).planStartAt = prevIso;
      this.downloaderStartTimeRejectedByMountpoint[mp] = true;
      return;
    }
    if (ms < min) {
      this.dialog.open(AcknowledgeDialogComponent, { data: `Recording Start Time can not be earlier than current system date/time. Keeping previous value (${prevLocal}).` });
      this.downInputs(mp).planStartAt = prevIso;
      this.downloaderStartTimeRejectedByMountpoint[mp] = true;
      return;
    }
    if (ms > max) {
      this.dialog.open(AcknowledgeDialogComponent, { data: `Recording Start Time can not be later than 7 days from now. Keeping previous value (${prevLocal}).` });
      this.downInputs(mp).planStartAt = prevIso;
      this.downloaderStartTimeRejectedByMountpoint[mp] = true;
      return;
    }
    this.downInputs(mp).planStartAt = iso;
    this.downloaderStartTimeRejectedByMountpoint[mp] = false;
  }

  isDownloaderStartTimeRejected(mp: string): boolean {
    this.ensureDownloaderDefaults(mp);
    return this.downloaderStartTimeRejectedByMountpoint[mp] === true;
  }

  isDownloaderStartTimeValid(mp: string): boolean {
    this.ensureDownloaderDefaults(mp);
    const iso = this.downInputs(mp).planStartAt;
    const d = new Date(iso);
    const ms = d.getTime();
    if (isNaN(ms)) return false;
    return ms >= this.nowFloorToMinuteMs() && ms <= this.maxScheduleMs();
  }

  // -------- Analyzer duration UI state --------
  analyzerDurationByMountpoint: { [key: string]: number } = {};
  private getAnalyzerDuration(mp: string): number {
    if (!this.analyzerDurationByMountpoint[mp]) this.analyzerDurationByMountpoint[mp] = 120;
    return this.analyzerDurationByMountpoint[mp];
  }

  // -------- Downloader duration select: curated presets (<24h) --------
  readonly durationPresetOptions: { minutes: number; label: string }[] = [
    { minutes: 5, label: '5 minutes' },
    { minutes: 10, label: '10 minutes' },
    { minutes: 15, label: '15 minutes' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 45, label: '45 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 90, label: '1 hour 30 minutes' },
    { minutes: 120, label: '2 hours' },
    { minutes: 180, label: '3 hours' },
    { minutes: 240, label: '4 hours' },
    { minutes: 300, label: '5 hours' },
    { minutes: 360, label: '6 hours' },
    { minutes: 420, label: '7 hours' },
    { minutes: 480, label: '8 hours' },
    { minutes: 540, label: '9 hours' },
    { minutes: 600, label: '10 hours' },
    { minutes: 720, label: '12 hours' },
    { minutes: 840, label: '14 hours' },
    { minutes: 960, label: '16 hours' },
    { minutes: 1200, label: '20 hours' },
    { minutes: 1320, label: '22 hours' },
    { minutes: 1440, label: '24 hours (max)' }
  ];

  getDurationSelection(mp: string): number {
    const secs = this.downloaderInputsByMountpoint[mp]?.durationSec ?? 0;
    const mins = Math.max(5, Math.min(1440, Math.round(secs / 60 / 5) * 5));
    return mins;
  }

  onDurationSelectionChange(mp: string, minutes: number): void {
    const clamped = Math.max(5, Math.min(1440, Number(minutes) || 5));
    this.downloaderInputsByMountpoint[mp].durationSec = clamped * 60;
  }
  setAnalyzerDuration(mp: string, v: any): void {
    const n = Number(v);
    this.analyzerDurationByMountpoint[mp] = (isFinite(n) && n > 0) ? n : 120;
  }

  onStartAnalysis(mp: MountPoint): void {
    const mountpoint = mp?.stMountPoint;
    if (!mountpoint) { return; }
    const duration = this.getAnalyzerDuration(mountpoint);
    this.userService.startAnalysis(mountpoint, duration).subscribe({
      next: (res) => {
        const body: any = res?.body ?? res; // observe:'response' returns HttpResponse
        const msg = body?.message || (body?.accepted ? "Hang tight! We’re pulling your data — it’ll be ready in about 3 minutes." : 'Analyzer request processed.');
        // 针对幂等窗口的友好提示
        const isIdempotent = /idempotency window|already running/i.test(msg);
        if (isIdempotent) {
          const friendly = `Another data pull is already in progress for ${mountpoint}. Please try again in about 3 minutes.`;
          this.dialog.open(AcknowledgeDialogComponent, { data: friendly });
          return;
        }
        // 若被接受且不是幂等/已在运行，则启动150秒倒计时（不弹出开始提示框）
        if ((body?.accepted === true)) {
          this.startAnalysisCountdown(mountpoint, duration);
          return;
        }
        // 其它情况（未显式accepted）可选提示一次
        // this.dialog.open(AcknowledgeDialogComponent, { data: 'Analyzer request processed.' });
      },
      error: (err) => {
        const raw = err?.error?.error || err.message;
        const friendly = /idempotency window|already running/i.test(raw)
          ? `Another data pull is already in progress for ${mountpoint}. Please try again in about 3 minutes.`
          : `Failed to start analysis: ${raw}`;
        this.dialog.open(AcknowledgeDialogComponent, { data: friendly });
      }
    });
  }

  onGetAnalysisReport(mp: MountPoint): void {
    const mountpoint = mp?.stMountPoint;
    if (!mountpoint) { return; }
    this.userService.getAnalysisReport(mountpoint).subscribe({
      next: (json) => {
        // 存到对应挂载点的展示区
        this.analysisJsonByMountpoint[mountpoint] = json;
        // 默认展开全部（同时清空节点 override）
        this.expandAll(mountpoint);
      },
      error: () => { /* 静默：无报告则不弹窗 */ }
    });
  }

  // -------- Downloader actions --------
  onStartDownloader(mp: MountPoint): void {
    if (!this.perms.hasAny(['downloader', 'ntrip-pro', 'ntrip-ent', 'ntrip-basic'])) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'You do not have permission to start downloader.' });
      return;
    }
    const mountpoint = mp?.stMountPoint;
    if (!mountpoint) { return; }
    if (this.downloaderLoading[mountpoint]) { return; }
    this.ensureDownloaderDefaults(mountpoint);

    if (this.isDownloaderStartTimeRejected(mountpoint) || !this.isDownloaderStartTimeValid(mountpoint)) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Recording Start Time is invalid. Please select a valid time before submitting.' });
      return;
    }
    const planStartAt = this.downInputs(mountpoint).planStartAt;
    const start = new Date(planStartAt);
    const ms = start.getTime();
    const min = this.nowFloorToMinuteMs();
    const max = this.maxScheduleMs();
    if (isNaN(ms)) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Invalid Recording Start Time.' });
      return;
    }
    if (ms < min) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Recording Start Time can not be earlier than current system date/time.' });
      return;
    }
    if (ms > max) {
      this.dialog.open(AcknowledgeDialogComponent, { data: 'Recording Start Time can not be later than 7 days from now.' });
      return;
    }

    const submittedStart = this.isoToLocalInputValue(planStartAt) || planStartAt;
    const durationSec = this.downloaderInputsByMountpoint[mountpoint].durationSec;
    const scheduleLeadMin = 1; // hard-coded preload lead time (minutes)
    this.downloaderLoading[mountpoint] = true;
    this.downloaderService.start(mountpoint, durationSec, planStartAt, scheduleLeadMin).subscribe({
      next: (res) => {
        this.downloaderLoading[mountpoint] = false;
        const body: any = (res as any)?.body ?? res;
        const msg = body?.message || (body?.accepted ? 'Recorder triggered (accepted).' : 'Downloader request processed.');
        const friendly = /idempotency window|already running|within idempotency/i.test(msg)
          ? `Recorder already running or scheduled to be run for ${mountpoint}`
          : msg;
        this.dialog.open(AcknowledgeDialogComponent, { data: `${friendly} (MountPoint: ${mountpoint}, Start: ${submittedStart})` });
        // Immediately reflect running/scheduled state in the file card
        this.downloaderUrlByMountpoint[mountpoint] = { running: true } as any;
        // Switch to manual refresh: do not auto-poll; user clicks "Refresh status" to update
      },
      error: (err) => {
        this.downloaderLoading[mountpoint] = false;
        const status = err?.status;
        const raw = err?.error?.error || err.message || 'Unknown Error';
        const msg = status === 0
          ? 'Network error: backend unreachable or request aborted. Please try again.'
          : `Failed to start downloader: ${raw}`;
        this.dialog.open(AcknowledgeDialogComponent, { data: `${msg} (MountPoint: ${mountpoint}, Start: ${submittedStart})` });
      }
    });
  }

  onGetDownloaderUrl(mp: MountPoint): void {
    const mountpoint = mp?.stMountPoint;
    if (!mountpoint) { return; }
    if (!this.perms.hasAny(['downloader', 'ntrip-pro', 'ntrip-ent', 'ntrip-basic'])) {
      // 无权限则不触发请求
      return;
    }
    this.downloaderService.getDownloaderUrl(mountpoint).subscribe({
      next: (out) => {
        this.downloaderUrlByMountpoint[mountpoint] = out as any;
        // Manual mode: no auto polling here
      },
      error: (err) => {
        const raw = err?.error?.error || err.message;
        // On error, clear local state to avoid stale running flag
        this.downloaderUrlByMountpoint[mountpoint] = null;
        // Manual mode: no auto retry
      }
    });
  }

  private startDownloaderPolling(mp: string): void {
    const key = mp;
    if (!key) return;
    if (this.downloaderPollTimers[key]) return; // already polling
    const tick = () => {
      this.downloaderService.getDownloaderUrl(key).subscribe({
        next: (out) => {
          this.downloaderUrlByMountpoint[key] = out as any;
          const running = (out as any)?.running;
          const hasUrl = !!(out as any)?.url;
          if (running) {
            this.downloaderPollTimers[key] = setTimeout(tick, 5000);
          } else {
            // finished, stop whether url exists or not
            this.stopDownloaderPolling(key);
          }
        },
        error: () => {
          // Retry briefly on error
          this.downloaderPollTimers[key] = setTimeout(tick, 5000);
        }
      });
    };
    this.downloaderPollTimers[key] = setTimeout(tick, 3000);
  }

  private stopDownloaderPolling(mp: string): void {
    const t = this.downloaderPollTimers[mp];
    if (t) {
      clearTimeout(t);
      delete this.downloaderPollTimers[mp];
    }
  }

  // -------- Pagination (1 item per page, sorted by name) --------
  currentPage: number = 1;
  readonly pageSize: number = 1;
  private sortedMountPoints: MountPoint[] = [];

  private buildSortedMountPoints(): void {
    const list = this.user?.mountPoints || [];
    this.sortedMountPoints = [...list].sort((a, b) => (a?.stMountPoint || '').localeCompare(b?.stMountPoint || ''));
  }

  private ensurePageBounds(): void {
    const total = this.totalPages;
    if (this.currentPage < 1) this.currentPage = 1;
    if (total > 0 && this.currentPage > total) this.currentPage = total;
  }

  get totalPages(): number {
    const total = this.sortedMountPoints.length;
    return total === 0 ? 0 : Math.ceil(total / this.pageSize);
  }

  get currentMountPoint(): MountPoint | undefined {
    if (this.sortedMountPoints.length === 0) return undefined;
    const index = (this.currentPage - 1) * this.pageSize;
    return this.sortedMountPoints[index];
  }

  goPrev(): void { if (this.currentPage > 1) { this.currentPage--; this.triggerFetchForCurrent(); } }
  goNext(): void { if (this.currentPage < this.totalPages) { this.currentPage++; this.triggerFetchForCurrent(); } }
  goToPage(p: number): void {
    this.currentPage = Math.min(Math.max(1, p), this.totalPages);
    // Clear selection when navigating to a different item
    this.selectedMountPoint = undefined;
    this.triggerFetchForCurrent();
  }

  // 暴露排序后的列表给模板使用
  get sortedList(): MountPoint[] { return this.sortedMountPoints; }

  // 从下拉框选择某个挂载点后跳页
  selectFromDropdown(mpName: string): void {
    const idx = this.sortedMountPoints.findIndex(m => (m?.stMountPoint || '') === mpName);
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.goToPage(page);
      this.selectedMountPoint = this.sortedMountPoints[idx];
      this.resetDetailsStateFor(this.selectedMountPoint?.stMountPoint);
      this.action = '';
      this.canEdit = false;
    }
  }

  // 首页/末页
  goFirst(): void { this.goToPage(1); }
  goLast(): void { this.goToPage(this.totalPages); }

  // 计算页码窗口（最多 5 个，围绕当前页）
  get pageWindow(): number[] {
    const total = this.totalPages;
    if (total <= 0) return [];
    const targetCount = Math.min(5, total);
    const windowRadius = Math.floor(targetCount / 2);
    let start = Math.max(1, this.currentPage - windowRadius);
    let end = Math.min(total, start + targetCount - 1);
    // 若不足 targetCount，再向前补齐
    start = Math.max(1, Math.min(start, end - targetCount + 1));
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    // 若仍不足（当 total < targetCount）已处理，无需补
    return pages;
  }

  // -------- Auto fetch analyzer summary for the current item --------
  private triggerFetchForCurrent(): void {
    const mp = this.currentMountPoint?.stMountPoint;
    if (!mp) return;
    // Ensure details start collapsed and editing off for the current item
    this.resetDetailsStateFor(mp);
    // Default select current item so expanded body can render bindings
    this.selectedMountPoint = this.currentMountPoint;
    this.action = 'show';
    // Analyzer 自动请求：在 analyzer 页面或显式开启时获取；
    // 为了在 user-mountpoint 页面显示 SNR/Map（当已有报告时），当 showMap/showSnr 开启时也尝试拉取一次摘要（静默失败）。
    if (this.isAnalyzerPage || this.showAnalyzer) {
      const summary = this.analyzerSummaryByMountpoint[mp];
      const hasFull = !!this.analysisJsonByMountpoint[mp];
      if (this.autoShowFullAnalysis) {
        // 只请求一次“完整报告”，在回调里顺便生成摘要，避免双请求造成 2 x 404
        if (!hasFull) {
          this.onGetAnalysisReport({ stMountPoint: mp } as any);
        } else {
          this.scheduleRenderForMountpoint(mp);
        }
      } else {
        if (summary) {
          this.scheduleRenderForMountpoint(mp);
        } else {
          this.fetchAnalyzerSummary(mp);
        }
      }
    } else if (this.showMap || this.showSnr) {
      // 非 analyzer 页面：当需要展示地图或 SNR 时
      const summary = this.analyzerSummaryByMountpoint[mp];
      if (summary) {
        // 已有摘要 → 可能是 DOM 切换后，需要重新渲染图表/地图
        this.scheduleRenderForMountpoint(mp);
      } else {
        // 无摘要 → 拉取一次（静默失败）
        this.fetchAnalyzerSummary(mp);
      }
    }
    // Downloader: 始终尝试拉取一次（内部会做权限判断）
    if (this.showDownloader) {
      this.onGetDownloaderUrl({ stMountPoint: mp } as any);
    }
  }

  private fetchAnalyzerSummary(mountpoint: string): void {
    if (this.analyzerSummaryRequested[mountpoint]) return;
    this.analyzerSummaryRequested[mountpoint] = true;
    this.userService.getAnalysisReport(mountpoint).subscribe({
      next: (json) => {
        const pick = (k: string) => this.findValueByKeyCaseInsensitive(json, k);
        const summary: { [k: string]: any } = {
          protocol: pick('protocol'),
          location: pick('location'),
          geodetic_height_meters: pick('geodetic_height_meters'),
          orthometric_height_meters: pick('orthometric_height_meters'),
          coordinate_system: pick('coordinate_system'),
          Average: pick('Average'),
          Average_top_50: pick('Average_top_50'),
          signal_quality: pick('signal_quality')
        };
        this.analyzerSummaryByMountpoint[mountpoint] = summary;
        // store generated_at if available for message box
        const genAt = pick('generated_at');
        if (genAt) {
          this.generatedAtByMountpoint[mountpoint] = String(genAt);
        }
        // 渲染（等待视图渲染完成）
        this.scheduleRenderForMountpoint(mountpoint);
      },
      error: () => {
        // 静默失败，不弹窗
      }
    });
  }

  // 在 DOM 就绪后渲染地图和图表，避免元素尚未出现导致渲染被跳过
  private scheduleRenderForMountpoint(mountpoint: string): void {
    const summary = this.analyzerSummaryByMountpoint[mountpoint];
    if (!summary) return;
    const mapId = `map_${mountpoint}`;
    const tryRender = (attempt: number) => {
      const hasMapElem = !!document.getElementById(mapId);
      if (hasMapElem) {
        this.renderMapForMountpoint(mountpoint, summary['location']);
      }
      if (hasMapElem) return;
      if (attempt > 120) return; // 最长约 2 秒等待
      requestAnimationFrame(() => tryRender(attempt + 1));
    };
    requestAnimationFrame(() => tryRender(0));
  }

  private findValueByKeyCaseInsensitive(data: any, key: string): any {
    if (data == null) return undefined;
    if (typeof data !== 'object') return undefined;
    const lowerKey = key.toLowerCase();
    if (Array.isArray(data)) {
      for (const item of data) {
        const v = this.findValueByKeyCaseInsensitive(item, key);
        if (v !== undefined) return v;
      }
      return undefined;
    }
    for (const k of Object.keys(data)) {
      if (k.toLowerCase() === lowerKey) return data[k];
      const v = this.findValueByKeyCaseInsensitive(data[k], key);
      if (v !== undefined) return v;
    }
    return undefined;
  }

  // Summary availability checker for template: require at least one meaningful field
  hasAnalyzerSummary(mp: string | undefined): boolean {
    if (!mp) return false;
    const s = this.analyzerSummaryByMountpoint[mp];
    if (!s || typeof s !== 'object') return false;
    const loc = s['location'];
    const avg = s['Average'];
    const gh = s['geodetic_height_meters'];
    const oh = s['orthometric_height_meters'];
    return (loc && String(loc).trim().length > 0) || (avg !== undefined) || (gh !== undefined) || (oh !== undefined);
  }

  // ---- Countdown: when it completes, load the latest analysis report for the same mountpoint ----
  private startAnalysisCountdown(mountpoint: string, totalSeconds?: number): void {
    this.clearAnalysisCountdown();
    // Use selected duration (seconds); fallback to previous/default 150s if invalid
    const secs = Number(totalSeconds);
    this.analysisTotalSeconds = (isFinite(secs) && secs > 0) ? Math.floor(secs) : this.analysisTotalSeconds || 150;
    this.analysisCountdownActive = true;
    // warm-up 10s: no visible progress, show "starting" text
    this.warmupRemaining = 10;
    this.analysisCountdownRemaining = this.analysisTotalSeconds;
    this.analysisCountdownMountpoint = mountpoint;
    const tick = () => {
      if (this.warmupRemaining > 0) {
        this.warmupRemaining = Math.max(0, this.warmupRemaining - 1);
      } else {
        this.analysisCountdownRemaining = Math.max(0, this.analysisCountdownRemaining - 1);
      }
      if (this.analysisCountdownRemaining <= 0) {
        this.clearAnalysisCountdown();
        // Keep focus and load latest report (no full page reload)
        this.persistFocusMountpoint(mountpoint);
        this.persistFocusToUrl(mountpoint);
        this.onGetAnalysisReport({ stMountPoint: mountpoint } as any);
        return;
      }
      this.analysisCountdownTimer = setTimeout(tick, 1000);
    };
    this.analysisCountdownTimer = setTimeout(tick, 1000);
  }

  private clearAnalysisCountdown(): void {
    if (this.analysisCountdownTimer) {
      clearTimeout(this.analysisCountdownTimer);
      this.analysisCountdownTimer = null;
    }
    this.analysisCountdownActive = false;
    this.analysisCountdownRemaining = 0;
    this.analysisCountdownMountpoint = '';
  }

  private persistFocusMountpoint(mp: string): void {
    try { localStorage.setItem(this.focusStorageKey, mp || ''); } catch {}
  }

  private persistFocusToUrl(mp: string): void {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(this.focusQueryParam, mp || '');
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }

  private restoreFocusFromUrlIfAny(): void {
    try {
      const url = new URL(window.location.href);
      const mp = url.searchParams.get(this.focusQueryParam);
      if (!mp) return;
      // 移除参数，避免后续干扰
      url.searchParams.delete(this.focusQueryParam);
      window.history.replaceState({}, '', url.toString());
      const idx = this.sortedMountPoints.findIndex(m => (m?.stMountPoint || '') === mp);
      if (idx >= 0) {
        const page = Math.floor(idx / this.pageSize) + 1;
        this.currentPage = page;
        this.selectedMountPoint = this.sortedMountPoints[idx];
        this.resetDetailsStateFor(this.selectedMountPoint?.stMountPoint);
        this.action = 'show';
        this.canEdit = false;
      }
    } catch {}
  }

  private restoreFocusedMountpointIfAny(): void {
    let target: string | null = null;
    try { target = localStorage.getItem(this.focusStorageKey); } catch { target = null; }
    if (!target) return;
    try { localStorage.removeItem(this.focusStorageKey); } catch {}
    const idx = this.sortedMountPoints.findIndex(m => (m?.stMountPoint || '') === target);
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.currentPage = page;
      this.selectedMountPoint = this.sortedMountPoints[idx];
      this.resetDetailsStateFor(this.selectedMountPoint?.stMountPoint);
      this.action = 'show';
      this.canEdit = false;
    }
  }

  // ---- 进度条显示辅助 ----
  getAnalysisPercent(): number {
    if (!this.analysisCountdownActive) return 0;
    // during warmup, keep progress 0
    if (this.warmupRemaining > 0) return 0;
    const done = Math.max(0, Math.min(this.analysisTotalSeconds, this.analysisTotalSeconds - this.analysisCountdownRemaining));
    return Math.round((done / this.analysisTotalSeconds) * 100);
  }

  private formatMMSS(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${this.pad(m)}:${this.pad(s)} `;
  }

  getAnalysisElapsedLabel(): string {
    const elapsedCore = this.analysisCountdownActive ? (this.analysisTotalSeconds - this.analysisCountdownRemaining) : 0;
    const elapsed = elapsedCore + (this.warmupRemaining > 0 ? 0 : 10);
    return this.formatMMSS(elapsed);
  }

  getAnalysisRemainingLabel(): string {
    const remain = this.analysisCountdownActive ? this.analysisCountdownRemaining : this.analysisTotalSeconds;
    return this.formatMMSS(remain);
  }

  // -------- Leaflet map rendering --------
  private parseLatLngFromLocation(loc: string | undefined): { lat: number, lng: number } | null {
    if (!loc || typeof loc !== 'string') return null;
    // 形如 "43.8933343273°N, 79.3084199450°W"
    const match = loc.match(/([\d.+-]+)°?\s*([NnSs])\s*,\s*([\d.+-]+)°?\s*([EeWw])/);
    if (!match) return null;
    let lat = parseFloat(match[1]);
    const ns = match[2].toUpperCase();
    let lng = parseFloat(match[3]);
    const ew = match[4].toUpperCase();
    if (ns === 'S') lat = -lat;
    if (ew === 'W') lng = -lng;
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
    return null;
  }

  getGoogleMapUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const t = mode === 'sat' ? 'k' : 'm';
    // 使用 query + t= 参数，实际行为更稳定
    return `https://www.google.com/maps?q=${c.lat},${c.lng}&z=18&t=${t}`;
  }

  // Bing Maps 外链（r=road, a=aerial）
  getBingMapUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const style = mode === 'sat' ? 'a' : 'r';
    const label = encodeURIComponent('Location');
    // sp=point.lat_long_Label 可显示点标记
    return `https://www.bing.com/maps?cp=${c.lat}~${c.lng}&lvl=18&style=${style}&sp=point.${c.lat}_${c.lng}_${label}`;
  }

  // --- 坐标系转换 ---
  private outOfChina(lat: number, lng: number): boolean {
    return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
  }
  private transformLat(x: number, y: number): number {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
    return ret;
  }
  private transformLng(x: number, y: number): number {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
    ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
    ret += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
    ret += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
    return ret;
  }
  private wgs84ToGcj02(lat: number, lng: number): { lat: number; lng: number } {
    if (this.outOfChina(lat, lng)) return { lat, lng };
    const a = 6378245.0;
    const ee = 0.00669342162296594323;
    let dLat = this.transformLat(lng - 105.0, lat - 35.0);
    let dLng = this.transformLng(lng - 105.0, lat - 35.0);
    const radLat = lat / 180.0 * Math.PI;
    let magic = Math.sin(radLat);
    magic = 1 - ee * magic * magic;
    const sqrtMagic = Math.sqrt(magic);
    dLat = (dLat * 180.0) / ((a * (1 - ee)) / (magic * sqrtMagic) * Math.PI);
    dLng = (dLng * 180.0) / (a / sqrtMagic * Math.cos(radLat) * Math.PI);
    return { lat: lat + dLat, lng: lng + dLng };
  }
  private gcj02ToBd09(lat: number, lng: number): { lat: number; lng: number } {
    const x_pi = Math.PI * 3000.0 / 180.0;
    const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * x_pi);
    const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * x_pi);
    return { lng: z * Math.cos(theta) + 0.0065, lat: z * Math.sin(theta) + 0.006 };
  }

  // Amap 外链（基于 GCJ-02）
  getAmapUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const g = this.wgs84ToGcj02(c.lat, c.lng);
    // Amap 网页未公开稳定卫星开关参数，统一使用 marker URI，确保坐标准确
    return `https://uri.amap.com/marker?position=${g.lng},${g.lat}&name=Location&callnative=0`;
  }

  // Baidu 外链（基于 BD-09）
  getBaiduUrl(mp: string, mode: 'sat' | 'map'): string {
    const c = this.coordsByMountpoint[mp];
    if (!c) return '#';
    const g = this.wgs84ToGcj02(c.lat, c.lng);
    const b = this.gcj02ToBd09(g.lat, g.lng);
    // 为保证落点稳定，统一使用百度 Marker API（进入页面用户可在界面切换卫星图层）
    return `https://api.map.baidu.com/marker?location=${b.lat},${b.lng}&title=Location&content=&output=html&zoom=18&coord_type=bd09ll`;
  }

  private renderMapForMountpoint(mountpoint: string, locationStr?: string): void {
    const coords = this.parseLatLngFromLocation(locationStr || '');
    const elemId = `map_${mountpoint}`;
    const elem = document.getElementById(elemId);
    if (!elem || !coords) return;
    // @ts-ignore - Leaflet added via script tag
    const Lref = (window as any).L;
    if (!Lref) return;
    // 若此前已创建过地图实例，先销毁
    if (this.mapInstances[mountpoint]?.remove) {
      try { this.mapInstances[mountpoint].remove(); } catch {}
      delete this.mapInstances[mountpoint];
    }
    // 如果容器上已有 Leaflet 挂载痕迹，清理后再建
    try { (elem as any)._leaflet_id = undefined; } catch {}
    elem.innerHTML = '';
    const map = Lref.map(elemId, {
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
      inertia: false,
      zoomAnimation: false,
      fadeAnimation: false
    });
    const street = Lref.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 14,
      attribution: '© OpenStreetMap'
    });
    street.addTo(map);
    Lref.marker([coords.lat, coords.lng]).addTo(map);
    map.setView([coords.lat, coords.lng], 15);
    // 存坐标供外链使用
    this.coordsByMountpoint[mountpoint] = { lat: coords.lat, lng: coords.lng };
    // 缓存实例，便于后续销毁
    this.mapInstances[mountpoint] = map;
    // 窗口大小变化时刷新
    setTimeout(() => map.invalidateSize(), 0);
  }

  // -------- Signal Quality chart helpers removed --------

  getAveragePercent(value: any): number {
    const n = Number(value);
    if (!isFinite(n)) return 0;
    const pct = (n / 63) * 100;
    return Math.max(0, Math.min(100, pct));
  }

}


